// Phase 129 Plan 04 Task 1 — Top nav (DESIGN.md frosted-glass dark variant).
//
// DESIGN.md specifies frosted-glass nav with `rgba(241, 244, 247, 0.8)` for
// the light retail surface. We invert it for the dark canvas — same backdrop
// blur + 80% opacity over the canvas color #1C1E21. Sticky top, subtle border
// for separation.
//
// Auth CTAs route directly through the backend /auth/login endpoint (which
// mints CSRF state + 302s to WorkOS-AuthKit), skipping the in-app LandingPage.
// authBaseUrl is environment-aware (staging → staging-api, production → api,
// local → ngrok app proxy).
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { Menu, X } from 'lucide-react';

import { emit } from '../lib/analytics';
import { authBaseUrl } from '../lib/handoff';
import { NavSolutionsDesktop, NavSolutionsMobile } from './nav-solutions';

export function Nav() {
  // Both "Start Free" and "Login" route through /auth/login — AuthKit's hosted
  // UI presents sign-up + sign-in side-by-side, so the same entry serves both.
  const authLoginHref = `${authBaseUrl()}/auth/login?returnTo=${encodeURIComponent('/')}`;
  const ctaHref = authLoginHref;
  const loginHref = authLoginHref;
  // Section links are fragment anchors on the homepage, but a bare `#built-for`
  // on another route (e.g. /docs) resolves to /docs#built-for and goes nowhere.
  // Off the homepage, prefix with `/` so the link navigates home and the browser
  // lands on the section; on the homepage keep the in-page anchor.
  const { pathname } = useLocation();
  const onHome = pathname === '/';
  const builtForHref = onHome ? '#built-for' : '/#built-for';
  const pricingHref = onHome ? '#pricing' : '/#pricing';
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const hamburgerRef = useRef<HTMLButtonElement | null>(null);

  // Close on Escape + outside-tap. We do NOT render a backdrop element because
  // the nav has `backdrop-blur-md`, which makes it the containing block for
  // any `position: fixed` descendant — so a `fixed inset-0` overlay would only
  // cover the nav, not the rest of the viewport. A document-level pointerdown
  // listener bypasses that constraint and works on touch + mouse + pen.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (dropdownRef.current?.contains(target)) return;
      if (hamburgerRef.current?.contains(target)) return;
      setMobileOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [mobileOpen]);

  // AIT-268 — close the hamburger sheet when the route changes. The Solutions
  // rows are real <Link>s, so a click navigates without unmounting the nav.
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const closeMobile = () => setMobileOpen(false);

  return (
    <nav className="sticky top-0 z-[60] backdrop-blur-md bg-[#1C1E21]/80 border-b border-white/10">
      <div className="mx-auto max-w-6xl flex items-center px-6 py-4 text-sm text-white/70">
        <Link
          to="/"
          className="flex items-center gap-2 text-lg font-medium text-white tracking-tight"
        >
          <img
            src="/logo-mark.svg"
            alt=""
            aria-hidden="true"
            className="h-6 w-auto"
          />
          <span>HookMyApp</span>
        </Link>
        <div className="hidden md:flex items-center gap-7 ml-10">
          <NavSolutionsDesktop />
          <a href={builtForHref} className="hover:text-white transition">
            Built For
          </a>
          <a href={pricingHref} className="hover:text-white transition">
            Pricing
          </a>
        </div>
        <div className="ml-auto flex items-center gap-3 md:gap-5">
          <Link
            to="/docs"
            className="hidden md:inline hover:text-white transition"
          >
            Docs
          </Link>
          <Link
            to="/affiliates"
            className="hidden md:inline hover:text-white transition"
          >
            Affiliates
          </Link>
          <span
            className="hidden md:inline-block h-5 w-px bg-white/15"
            aria-hidden="true"
          />
          <a
            href={loginHref}
            onClick={() =>
              emit('cta_clicked', {
                cta_location: 'nav',
                cta_text: 'Login',
                destination_url: loginHref,
              })
            }
            className="hidden md:inline text-white font-medium hover:opacity-80 transition"
          >
            Login
          </a>
          <a
            href={ctaHref}
            onClick={() =>
              emit('cta_clicked', {
                cta_location: 'nav',
                cta_text: 'Start Free',
                destination_url: ctaHref,
              })
            }
            className="rounded-[100px] bg-[#0064E0] hover:bg-[#0143B5] active:bg-[#004BB9] px-4 py-2 md:px-5 text-sm font-medium text-white transition whitespace-nowrap"
          >
            Start Free
          </a>
          <span
            className="md:hidden h-5 w-px bg-white/15"
            aria-hidden="true"
          />
          <button
            ref={hamburgerRef}
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav-sheet"
            className="md:hidden inline-flex size-10 items-center justify-center rounded-full text-white hover:bg-white/10 transition"
          >
            {mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown — anchored card under the hamburger. Solid surface
          so it reads cleanly over any scrolled page content. md:hidden so it
          never renders on desktop. Outside-tap to close is handled by the
          document-level pointerdown listener (see the Effect above) — we
          can't use a `fixed inset-0` overlay here because the nav has
          `backdrop-blur-md`, which traps fixed descendants inside its
          containing block. */}
      {mobileOpen ? (
          <div
            ref={dropdownRef}
            id="mobile-nav-sheet"
            className="md:hidden absolute inset-x-0 top-full z-[70] origin-top border-b border-white/10 bg-[#181A1B] shadow-[0_16px_40px_rgba(0,0,0,0.55),0_4px_12px_rgba(0,0,0,0.35)] overflow-hidden animate-[nav-pop_140ms_ease-out]"
          >
            <div className="mx-auto max-w-6xl flex flex-col py-2 text-base">
              <NavSolutionsMobile onNavigate={closeMobile} />
              <a
                href={builtForHref}
                onClick={closeMobile}
                className="px-6 py-3 text-white/80 hover:text-white hover:bg-white/[0.04] transition"
              >
                Built For
              </a>
              <a
                href={pricingHref}
                onClick={closeMobile}
                className="px-6 py-3 text-white/80 hover:text-white hover:bg-white/[0.04] transition"
              >
                Pricing
              </a>
              <Link
                to="/docs"
                onClick={closeMobile}
                className="px-6 py-3 text-white/80 hover:text-white hover:bg-white/[0.04] transition"
              >
                Docs
              </Link>
              <Link
                to="/affiliates"
                onClick={closeMobile}
                className="px-6 py-3 text-white/80 hover:text-white hover:bg-white/[0.04] transition"
              >
                Affiliates
              </Link>
              <span className="my-1 mx-6 h-px bg-white/10" aria-hidden="true" />
              <a
                href={loginHref}
                onClick={() => {
                  emit('cta_clicked', {
                    cta_location: 'nav',
                    cta_text: 'Login',
                    destination_url: loginHref,
                  });
                  closeMobile();
                }}
                className="px-6 py-3 text-white font-medium hover:bg-white/[0.04] transition"
              >
                Login
              </a>
            </div>
          </div>
      ) : null}
    </nav>
  );
}
